<template>
	<div class="preview-wrapper">
		<div class="d-flex my-2 preview-actions">
			<v-btn :disabled="allChangesAreCancelled || changes.length === 0" class="mr-2" variant="text" prepend-icon="mdi-undo" @click="undoChange">
				{{ t('undo') }}
			</v-btn>
			<v-btn
				v-if="isLastChangeUndone && undoneChanges.length > 0"
				variant="text"
				prepend-icon="mdi-redo"
				@click="redoChange"
				:disabled="allChangesAreCancelled"
			>
				{{ t('redo') }}
			</v-btn>
		</div>
		<div class="preview-list">
			<div v-for="change in reversedChanges" :key="change._id" class="preview-item" :class="{ 'preview-item--cancelled ': isCancelled(change) }">
				<div v-if="change.status != 'published'" @click="selectChangeLog(change)">
					<!-- Card Overlay for Cancelled State -->
					<div v-if="isCancelled(change)" class="preview-item-overlay"></div>

					<!-- Header Section -->
					<div class="preview-item-content preview-change-header" @click="toggleExpanded(change._id)">
						<div class="preview-item-header mb-1">
							<span class="preview-item-duty" :class="{ 'text-strikethrough': isCancelled(change) || isUndone(change) }">
								{{ t(change.messageKey, change.messageParams) }}
							</span>
							<v-icon
								v-if="change.diffsString.length"
								size="20"
								class="preview-chevron"
								:class="{ 'preview-chevron--open': expandedMap[change._id] }"
							>
								mdi-chevron-down
							</v-icon>
						</div>
						<div class="d-flex align-center">
							<v-icon size="16" color="#9e9e9e">mdi-calendar-month</v-icon>
							<span class="preview-item-title ml-1">{{ formatDate(dayStore.daysRef[change.day]) }}</span>
						</div>
					</div>

					<!-- Diff Count Toggle -->
					<div v-if="change.diffsString.length" class="preview-change-count" @click="toggleExpanded(change._id)">
						{{ change.diffsString.length }} {{ change.diffsString.length === 1 ? t('change') : t('changes') }}
					</div>

					<!-- Details Section -->
					<div v-if="change.diffsString.length && expandedMap[change._id]" class="preview-item-details">
						<div v-for="diff in change.diffsString" :key="diff.key" class="preview-item-meta">
							<span class="preview-item-diff-key" :class="{ 'text-strikethrough': isCancelled(change) }">
								{{ Object.hasOwn(diff, 'label') ? diff.label : formatDiffKey(diff.key) }}
							</span>
							<div class="d-flex flex-wrap">
								<span class="mr-1">:</span>
								<span class="preview-item-old-value" :class="{ 'text-strikethrough': isCancelled(change) }">
									<template v-if="diff.oldValue"> {{ t(diff.valueKey ? diff.valueKey + diff.oldValue : diff.oldValue) }}</template>
									<em v-else>{{ t('empty') }}</em>
								</span>

								<span class="preview-item-arrow">→</span>
								<span class="preview-item-new-value" :class="{ 'text-strikethrough': isCancelled(change) }">
									<template v-if="diff.newValue">
										{{ t(diff.valueKey ? diff.valueKey + diff.newValue : diff.newValue) }}
									</template>
									<em v-else>{{ t('empty') }}</em>
								</span>
							</div>
						</div>
					</div>
					<!-- Comment Section -->
					<div class="preview-comment">
						<span class="comment__icon">
							<v-img contain :src="messageSVG" alt="" width="20" height="20" />
						</span>
						<input
							class="preview-comment-input"
							type="text"
							:disabled="isCancelled(change)"
							:placeholder="t('enter_comment_placeholder')"
							:value="commentValue(change)"
							@click.stop
							@input="onCommentInput(change, $event)"
							@blur="flushComment(change)"
						/>
					</div>

					<!-- Cancelled Warning Banner -->
					<div v-if="isCancelled(change) && change.noteKey" class="preview-warning-banner">
						<v-icon color="#ef4444" size="18" class="mr-2">mdi-alert</v-icon>
						<span class="preview-warning-text">
							{{ t(change.noteKey, change.noteParams) }}
						</span>
					</div>
				</div>
			</div>
		</div>

		<v-expansion-panels v-if="undoneChanges.length > 0" class="preview-undone">
			<v-expansion-panel class="undo-div" :title="t('preview.undone_actions') + ` (${undoneChanges.length})`">
				<template #text>
					<div v-for="change in undoneChanges" :key="change._id" class="preview-item" :class="{ 'preview-item--undone': isUndone(change) }">
						<div v-if="change.status != 'published'" @click="selectChangeLog(change)">
							<!-- Card Overlay for Cancelled State -->
							<div v-if="isUndone(change)" class="preview-item-overlay"></div>

							<!-- Header Section -->
							<div class="preview-item-content preview-change-header" @click="toggleExpanded(change._id)">
								<div class="preview-item-header mb-1">
									<span class="preview-item-duty" :class="{ 'text-strikethrough': isUndone(change) }">
										{{ t(change.messageKey, change.messageParams) }}
									</span>
									<v-icon
										v-if="change.diffsString.length"
										size="20"
										class="preview-chevron"
										:class="{ 'preview-chevron--open': expandedMap[change._id] }"
									>
										mdi-chevron-down
									</v-icon>
								</div>
								<div class="d-flex align-center">
									<v-icon size="16" color="#9e9e9e">mdi-calendar-month</v-icon>
									<span class="preview-item-title ml-1">{{ formatDate(dayStore.daysRef[change.day]) }}</span>
								</div>
							</div>

							<!-- Diff Count Toggle -->
							<div v-if="change.diffsString.length" class="preview-change-count" @click="toggleExpanded(change._id)">
								{{ change.diffsString.length }} {{ change.diffsString.length === 1 ? t('change') : t('changes') }}
							</div>

							<!-- Details Section -->
							<div v-if="change.diffsString.length && expandedMap[change._id]" class="preview-item-details">
								<div v-for="diff in change.diffsString" :key="diff.key" class="preview-item-meta">
									<span class="preview-item-diff-key">
										{{ formatDiffKey(diff.key) }}
									</span>
									<div class="d-flex flex-wrap">
										<span class="mr-1">:</span>
										<span class="preview-item-old-value">
											<template v-if="diff.oldValue"> {{ t(diff.valueKey ? diff.valueKey + diff.oldValue : diff.oldValue) }}</template>
											<em v-else>{{ t('empty') }}</em>
										</span>

										<span class="preview-item-arrow">→</span>
										<span class="preview-item-new-value">
											<template v-if="diff.newValue">
												{{ t(diff.valueKey ? diff.valueKey + diff.newValue : diff.newValue) }}
											</template>
											<em v-else>{{ t('empty') }}</em>
										</span>
									</div>
								</div>
							</div>
							<!-- Comment Section -->
							<div v-if="!isUndone" class="preview-comment">
								<span class="comment__icon">
									<v-img contain :src="messageSVG" alt="" width="20" height="20" />
								</span>
								<input
									class="preview-comment-input"
									type="text"
									:placeholder="t('enter_comment_placeholder')"
									:value="commentValue(change)"
									@click.stop
									@input="onCommentInput(change, $event)"
									@blur="flushComment(change)"
								/>
							</div>
						</div>
					</div>
				</template>
			</v-expansion-panel>
		</v-expansion-panels>
	</div>
</template>

<script setup lang="ts">
import { reactive, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import type { DutyChangePreviewResponse } from '@/rennovation/planning-view/types/api/preview-response';
import { useDateTime } from '../../../../../../../components/composables/useDateTime.js';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import messageSVG from '@/assets/message.svg';
import { usePreviewStore } from '@/rennovation/planning-view/store/usePreviewStore';

type Change = DutyChangePreviewResponse['changes'][number] & {
	status?: string;
	warningMessage?: string;
};

const { t } = useI18n();
const { formatDate } = useDateTime();
const dayStore = useDayStore();
const { changes, updateChanges, undoChange, redoChange } = useDutyChangePreview();

const reversedChanges = computed(() => [...changes.value].reverse().filter((change) => change.status !== 'undone'));
const undoneChanges = computed(() => [...changes.value].reverse().filter((change) => change.status === 'undone'));
const expandedMap = reactive<Record<string, boolean>>({});
const commentMap = reactive<Record<string, string>>({});

function isCancelled(change: Change): boolean {
	return change.status === 'cancelled';
}

function isUndone(change: Change): boolean {
	return change.status === 'undone';
}

const lastChange = computed(() => changes.value.at(-1));
const isLastChangeUndone = computed(() => !!lastChange.value && (isUndone(lastChange.value) || isCancelled(lastChange.value)));
const allChangesAreCancelled = computed(() => {
	return changes.value.length > 0 && changes.value.every((item) => item.status === 'cancelled');
});

function toggleExpanded(id: string): void {
	expandedMap[id] = !expandedMap[id];
}

function commentValue(change: Change): string {
	return commentMap[change._id] ?? change.comment ?? '';
}

const COMMENT_DEBOUNCE_MS = 600;
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

function onCommentInput(change: Change, event: Event): void {
	commentMap[change._id] = (event.target as HTMLInputElement).value;

	const existing = debounceTimers.get(change._id);
	if (existing) clearTimeout(existing);

	const timer = setTimeout(() => {
		debounceTimers.delete(change._id);
		saveIfChanged(change);
	}, COMMENT_DEBOUNCE_MS);
	debounceTimers.set(change._id, timer);
}

function flushComment(change: Change): void {
	const existing = debounceTimers.get(change._id);
	if (existing) {
		clearTimeout(existing);
		debounceTimers.delete(change._id);
	}
	saveIfChanged(change);
}

async function saveIfChanged(change: Change): Promise<void> {
	const trimmed = commentValue(change).trim();
	if (trimmed === (change.comment ?? '')) return;

	await updateChanges(changes.value.map((c) => (c._id === change._id ? { ...c, comment: trimmed } : c)));
}

function formatDiffKey(key: string): string {
	const match = key.match(/^(preview_diff_key_break_.+?)_(\d+)$/);

	if (match) {
		const [, translationKey, breakNumber] = match;
		return `${t(translationKey)} ${breakNumber}`;
	}

	return t(key);
}

const selectChangeLog = (change) => {
	usePreviewStore().selectedChangeLog = change;
	usePreviewStore().cardClicked++;
};

onBeforeUnmount(() => {
	debounceTimers.forEach((timer) => clearTimeout(timer));
	debounceTimers.clear();
});
</script>
<style scoped>
.text-strikethrough {
	text-decoration: line-through;
}

.preview-item-duty {
	font-family: Roboto;
	font-weight: 700;
	font-size: 16px;
	line-height: 20px;
	letter-spacing: 0.4px;
	vertical-align: bottom;
}

.comment__icon {
	flex-shrink: 0;
	flex-grow: 0;
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.preview-item {
	position: relative;
	border: 1px solid #e0e0e0;
	padding: 0;
	background-color: #ffffff;
	border-radius: 8px;
	margin-bottom: 12px;
	transition: all 0.2s ease;
	cursor: pointer;
}

/* Cancelled State Styles */
.preview-item--cancelled {
	border: 1px solid #fca5a5;
	background-color: #fafafa;
}

.preview-item--cancelled .preview-item-diff-key,
.preview-item--cancelled .preview-item-old-value,
.preview-item--cancelled .preview-item-new-value,
.preview-item--cancelled .preview-item-arrow {
	color: #f87171;
}

.preview-item--cancelled .preview-comment {
	border-top: 1px solid #fee2e2;
	background-color: #fcfcfc;
}

.preview-item--undone {
	border: 1px dashed #bdbdbd;
	background-color: #fafafa;
}

.preview-item--undone .preview-item-duty {
	color: #757575;
}

.preview-item--undone .preview-item-title,
.preview-item--undone .preview-chevron {
	color: #757575;
}

.preview-item--undone .preview-item-content,
.preview-item--undone .preview-item-details,
.preview-item--undone .preview-change-count {
	border-bottom-color: #e0e0e0;
}

.preview-item--undone .preview-item-diff-key,
.preview-item--undone .preview-item-old-value,
.preview-item--undone .preview-item-arrow {
	color: #9e9e9e;
}

.preview-item--undone .preview-item-new-value {
	color: #424242;
	font-weight: 700;
}

.preview-item-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(255, 255, 255, 0.35);
	border-radius: 8px;
	pointer-events: none;
	z-index: 1;
}

.preview-item-title {
	font-family: Roboto;
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.25px;
	color: #757575;
}

.preview-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.preview-item-meta {
	display: flex;
	align-items: flex-start;
	font-family: Roboto, sans-serif;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	letter-spacing: 0.25px;
	color: #757575;
}

.preview-item-content {
	padding: 10px 12px;
	border-bottom: 1px solid #eeeeee;
}

.preview-item-details {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 10px 12px;
	border-bottom: 1px solid #eeeeee;
}

.preview-chevron {
	color: #9e9e9e;
	transition: transform 0.15s ease;
}

.preview-chevron--open {
	transform: rotate(180deg);
}

.preview-change-count {
	padding: 10px 12px;
	border-bottom: 1px solid #eeeeee;
	font-family: Roboto;
	font-weight: 400;
	font-size: 14px;
	color: #212121;
	cursor: pointer;
}

.preview-comment {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	background-color: #fafafa;
	position: relative;
	z-index: 2;
}

.preview-comment-input {
	flex: 1;
	border: none;
	background: transparent;
	outline: none;
	font-family: Roboto;
	font-size: 12px;
	color: #212121;
}

.preview-comment-input::placeholder {
	color: #9e9e9e;
}

.preview-comment-input:disabled {
	cursor: not-allowed;
	color: #9e9e9e;
}

.preview-warning-banner {
	display: flex;
	align-items: center;
	padding: 10px 12px;
	border-top: 1px solid #fee2e2;
	background-color: #fff5f5;
	border-radius: 0 0 8px 8px;
	position: relative;
	z-index: 2;
}

.preview-warning-text {
	font-family: Roboto;
	font-size: 13px;
	color: #ef4444;
	line-height: 1.4;
}

.preview-item-diff-key {
	width: 100px;
	flex-shrink: 0;
	font-weight: 400;
	color: #757575;
	text-transform: capitalize;
	overflow-wrap: anywhere;
	align-self: flex-start;
	word-break: break-word;
}

.preview-item-old-value {
	font-weight: 400;
	color: #616161;
}

.preview-item-arrow {
	margin: 0 6px;
	color: #616161;
}

.preview-item-new-value {
	font-weight: 500;
	color: #000000de;
}

.undo-div {
	background-color: #f1f5f9;
	font-family: Roboto;
	font-weight: 600;
	font-style: SemiBold;
	font-size: 14px;

	line-height: 24px;
	letter-spacing: 0.5px;
}

.undo-div :deep(.v-expansion-panel-text__wrapper) {
	padding-left: 4px;
	padding-right: 4px;
	padding-bottom: 4px;
}

.preview-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.preview-actions {
	flex: 0 0 auto;
}

.preview-list {
	flex: 1 1 0;
	min-height: 0;
	overflow-y: auto;
}

.preview-undone {
	flex: 0 0 auto;
	max-height: 50%;
	overflow-y: auto;
	padding-top: 8px;
}
</style>
